import { useEffect, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';

import { FormQuestion, FormUser } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { KatProgress } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';

import { isWeblabSegmentPagesEnabled } from 'src/clients/weblab/weblab-client';
import { INTAKE_FORM_KEYS } from 'src/components/pages/intake-form/FormEnums';
import { COMMON_BUNDLE_NAME, INFORMATION_FORM_STORAGE, SUBMITTING_ARIA_LABEL } from 'src/constants';
import { ROUTES } from 'src/enums/Routes';
import { t } from 'src/helpers';
import { useCreditsInterestFormData } from 'src/hooks/useCreditsInterestFormData';

import { CreditsInterestFormContext, IntakeFormContext } from './IntakeFormContext';

export const EXPECTED_QUESTIONS = isWeblabSegmentPagesEnabled
  ? ['NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION', 'SUBQUESTION_1', 'MEASURE_AND_REPORT_CRITERIA_QUESTION']
  : [
      'NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION',
      'SUBQUESTION_1',
      'MEASURE_AND_REPORT_CRITERIA_QUESTION',
      'DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION',
      'SUBQUESTION_3',
    ];

function hasCompleteStep1Data(data: Partial<Record<string, any>>): boolean {
  const name = data?.[INTAKE_FORM_KEYS.NAME];
  const email = data?.[INTAKE_FORM_KEYS.EMAIL];
  const orgName = data?.[INTAKE_FORM_KEYS.ORGANIZATION_NAME];
  const relation = data?.[INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP];
  const hasName = typeof name === 'string' && name.trim().length > 0;
  const hasEmail = typeof email === 'string' && email.trim().length > 0;
  const hasOrg = typeof orgName === 'string' && orgName.trim().length > 0;
  const hasRelation = Array.isArray(relation) && relation.length > 0;
  return hasName && hasEmail && hasOrg && hasRelation;
}

export const IntakeFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [commonBundle, isCommonBundleLoading] = useBundle(COMMON_BUNDLE_NAME);

  const { organizationData, buyerCriteriaData, isLoading, error } = useCreditsInterestFormData(commonBundle);

  // Provide minimal context data for existence checks (must be before early returns)
  const contextData: CreditsInterestFormContext = useMemo(() => {
    // On error: provide undefined (hasStep2 = false)
    if (error) {
      return { partialUser: undefined, partialQuestions: undefined };
    }

    // If we have organization data: provide minimal user object for existence check
    const user: FormUser | undefined =
      organizationData.name || organizationData.email
        ? {
            name: organizationData.name,
            email: organizationData.email,
            company: organizationData.organizationName,
          }
        : undefined;

    // If we have criteria data: provide minimal questions array for existence check
    const questions: FormQuestion[] | undefined =
      buyerCriteriaData.question1 && buyerCriteriaData.question2
        ? [{ questionId: 'q1', questionResponse: buyerCriteriaData.question1, questionText: '' }]
        : undefined;

    return { user, questions };
  }, [error, organizationData, buyerCriteriaData]);

  useEffect(() => {
    // Wait for loading to complete
    if (isLoading) return;

    // Preserve incoming navigation state through redirects
    const incomingState = location.state;

    // Check if API has data (user completed forms)
    const hasApiData =
      !error &&
      organizationData.name &&
      organizationData.email &&
      buyerCriteriaData.question1 &&
      buyerCriteriaData.question2;

    if (hasApiData) {
      // User has completed forms → go to My Account
      navigate(ROUTES.MY_ACCOUNT_PAGE, { replace: true, state: incomingState });
      return;
    }

    // No API data → check localStorage for step 1
    const step1Raw = localStorage.getItem(INFORMATION_FORM_STORAGE);
    const hasStep1FromStorage = (() => {
      if (!step1Raw) return false;
      try {
        const data = JSON.parse(step1Raw);
        return hasCompleteStep1Data(data as any);
      } catch {
        return false;
      }
    })();

    // Route based on localStorage
    if (hasStep1FromStorage) {
      // Step 1 complete → go to Step 2, but only if not explicitly on Step 1
      if (location.pathname !== ROUTES.INTAKE_PAGE_STEP_2 && location.pathname !== ROUTES.INTAKE_PAGE_STEP_1) {
        navigate(ROUTES.INTAKE_PAGE_STEP_2, { replace: true, state: incomingState });
      }
    } else if (location.pathname !== ROUTES.INTAKE_PAGE_STEP_1) {
      // Nothing complete → go to Step 1
      navigate(ROUTES.INTAKE_PAGE_STEP_1, { replace: true, state: incomingState });
    }
  }, [isLoading, error, organizationData, buyerCriteriaData, navigate, location.pathname, location.state]);

  if (isLoading || isCommonBundleLoading) {
    return (
      <div role="status">
        <KatProgress
          katAriaLabel={t(commonBundle, SUBMITTING_ARIA_LABEL)}
          indeterminate
          size="medium"
          type="circular"
        />
      </div>
    );
  }

  return (
    <IntakeFormContext.Provider value={contextData}>
      <Outlet />
    </IntakeFormContext.Provider>
  );
};
